/**
 * UK registration mark (VRM) helpers: normalisation, validation, display formatting
 * and decoding of the age identifier on dated plate formats.
 */

export function normaliseVrm(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export type VrmValidation = { ok: true; vrm: string } | { ok: false; vrm: string; reason: string };

export function validateVrm(input: string): VrmValidation {
  const vrm = normaliseVrm(input);
  if (vrm.length < 2) return { ok: false, vrm, reason: 'That registration looks too short.' };
  if (vrm.length > 7) return { ok: false, vrm, reason: 'UK registrations are at most 7 characters.' };
  if (!/[0-9]/.test(vrm) || !/[A-Z]/.test(vrm)) {
    return { ok: false, vrm, reason: 'UK registrations contain both letters and numbers.' };
  }
  return { ok: true, vrm };
}

export type PlateFormat = 'current' | 'prefix' | 'suffix' | 'northern-ireland' | 'dateless';

const CURRENT = /^([A-Z]{2})([0-9]{2})([A-Z]{3})$/;
const PREFIX = /^([A-Z])([0-9]{1,3})([A-Z]{3})$/;
const SUFFIX = /^([A-Z]{3})([0-9]{1,3})([A-Z])$/;
const NORTHERN_IRELAND = /^([A-Z]{1,3})([0-9]{1,4})$/;

export function plateFormat(vrm: string): PlateFormat {
  const v = normaliseVrm(vrm);
  if (CURRENT.test(v)) return 'current';
  if (PREFIX.test(v)) return 'prefix';
  if (SUFFIX.test(v)) return 'suffix';
  const ni = NORTHERN_IRELAND.exec(v);
  if (ni && /[IZ]/.test(ni[1] ?? '')) return 'northern-ireland';
  return 'dateless';
}

/** Display with the conventional space, e.g. AB12CDE → "AB12 CDE". */
export function formatVrm(vrm: string): string {
  const v = normaliseVrm(vrm);
  const m = CURRENT.exec(v);
  if (m) return `${m[1]}${m[2]} ${m[3]}`;
  const p = PREFIX.exec(v);
  if (p) return `${p[1]}${p[2]} ${p[3]}`;
  const s = SUFFIX.exec(v);
  if (s) return `${s[1]} ${s[2]}${s[3]}`;
  const ni = NORTHERN_IRELAND.exec(v);
  if (ni) return `${ni[1]} ${ni[2]}`;
  return v;
}

export interface PlateAge {
  format: PlateFormat;
  /** Human label for the issue period, e.g. "Sep 2008 – Feb 2009". */
  label: string;
  from: Date;
  /** Exclusive end of the issue period. */
  to: Date;
}

const utc = (y: number, m: number) => new Date(Date.UTC(y, m - 1, 1));
const monthLabel = (d: Date) =>
  d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });

function periodLabel(from: Date, to: Date): string {
  const last = new Date(to.getTime() - 24 * 60 * 60 * 1000);
  return `${monthLabel(from)} – ${monthLabel(last)}`;
}

// Prefix letters (Aug 1983 – Aug 2001). S was the last August issue; from 1999 issues moved to March/September.
const PREFIX_PERIODS: Record<string, [number, number, number, number]> = {
  A: [1983, 8, 1984, 8], B: [1984, 8, 1985, 8], C: [1985, 8, 1986, 8], D: [1986, 8, 1987, 8],
  E: [1987, 8, 1988, 8], F: [1988, 8, 1989, 8], G: [1989, 8, 1990, 8], H: [1990, 8, 1991, 8],
  J: [1991, 8, 1992, 8], K: [1992, 8, 1993, 8], L: [1993, 8, 1994, 8], M: [1994, 8, 1995, 8],
  N: [1995, 8, 1996, 8], P: [1996, 8, 1997, 8], R: [1997, 8, 1998, 8], S: [1998, 8, 1999, 3],
  T: [1999, 3, 1999, 9], V: [1999, 9, 2000, 3], W: [2000, 3, 2000, 9], X: [2000, 9, 2001, 3],
  Y: [2001, 3, 2001, 9],
};

// Suffix letters (1963 – Jul 1983).
const SUFFIX_PERIODS: Record<string, [number, number, number, number]> = {
  A: [1963, 1, 1964, 1], B: [1964, 1, 1965, 1], C: [1965, 1, 1966, 1], D: [1966, 1, 1967, 1],
  E: [1967, 1, 1967, 8], F: [1967, 8, 1968, 8], G: [1968, 8, 1969, 8], H: [1969, 8, 1970, 8],
  J: [1970, 8, 1971, 8], K: [1971, 8, 1972, 8], L: [1972, 8, 1973, 8], M: [1973, 8, 1974, 8],
  N: [1974, 8, 1975, 8], P: [1975, 8, 1976, 8], R: [1976, 8, 1977, 8], S: [1977, 8, 1978, 8],
  T: [1978, 8, 1979, 8], V: [1979, 8, 1980, 8], W: [1980, 8, 1981, 8], X: [1981, 8, 1982, 8],
  Y: [1982, 8, 1983, 8],
};

/** Decode the issue period encoded in a dated plate, or null for dateless / NI / Q plates. */
export function decodePlateAge(vrm: string): PlateAge | null {
  const v = normaliseVrm(vrm);
  const format = plateFormat(v);
  if (format === 'current') {
    const id = Number(CURRENT.exec(v)?.[2]);
    if (id >= 2 && id <= 50) {
      const from = utc(2000 + id, 3);
      const to = utc(2000 + id, 9);
      return { format, from, to, label: periodLabel(from, to) };
    }
    if (id >= 51 && id <= 99) {
      const year = 2000 + id - 50;
      const from = utc(year, 9);
      const to = utc(year + 1, 3);
      return { format, from, to, label: periodLabel(from, to) };
    }
    return null;
  }
  const table = format === 'prefix' ? PREFIX_PERIODS : format === 'suffix' ? SUFFIX_PERIODS : null;
  if (!table) return null;
  const letter = format === 'prefix' ? v[0] : v[v.length - 1];
  const period = letter ? table[letter] : undefined;
  if (!period) return null;
  const from = utc(period[0], period[1]);
  const to = utc(period[2], period[3]);
  return { format, from, to, label: periodLabel(from, to) };
}

export type PlateAgeCheck =
  | { status: 'matches'; plateAge: PlateAge }
  | { status: 'older-plate'; plateAge: PlateAge }
  | { status: 'newer-plate'; plateAge: PlateAge }
  | { status: 'dateless'; plateAge: null };

/**
 * Compare the plate's age identifier with the first registration date. A plate older than the car is
 * a normal private/cherished transfer; a plate newer than the car is unusual (e.g. an import registered
 * later, or a re-registration) and worth asking about.
 */
export function checkPlateAge(vrm: string, firstRegistered: string | Date | undefined): PlateAgeCheck {
  const plateAge = decodePlateAge(vrm);
  if (!plateAge || !firstRegistered) return { status: 'dateless', plateAge: null };
  const reg = typeof firstRegistered === 'string' ? new Date(firstRegistered) : firstRegistered;
  if (Number.isNaN(reg.getTime())) return { status: 'dateless', plateAge: null };
  // Allow a month of slack either side for pre-registration and registration processing.
  const slack = 31 * 24 * 60 * 60 * 1000;
  if (reg.getTime() < plateAge.from.getTime() - slack) return { status: 'newer-plate', plateAge };
  if (reg.getTime() >= plateAge.to.getTime() + slack) return { status: 'older-plate', plateAge };
  return { status: 'matches', plateAge };
}
